import { computed } from 'vue';
import { storeToRefs } from 'pinia';
import { useProduct } from '~/modules/product/composables/useProduct';
import useFeatureEligibility from '~/modules/featureControl/composables/useFeatureEligibility';
import { useProductFlags } from '~/modules/featureControl/composables/useProductFlags';
import useUpc from '~/modules/upc/composables/useUpc';
import useTraits from '~/composables/useTraits';
import useConsolidatedMemberStore from '~/stores/useConsolidatedMemberStore';

export const SELLER_NAME_MAX_CHARS = 30;

/**
 * Formats the seller name
 */
export function formatter(value: string): string {
  return value.slice(0, SELLER_NAME_MAX_CHARS).replace(/"/g, '');
}

export function useMarketPlace() {
  // Get Product Id
  const { productData } = useProduct();

  // Get Product Flags
  const { isMasterProductFlag } = useProductFlags();
  const { marketPlaceItemEnabled, isShopTheSetExpEnabled } = useFeatureEligibility();
  const { memberFeatureEligible } = storeToRefs(useConsolidatedMemberStore());
  const isMarketPlaceEnabled = !isMasterProductFlag || isShopTheSetExpEnabled;

  // Get Selected UPC
  const { selectedUpcId, upcData } = useUpc();

  const { selectedColorId } = useTraits();

  // Compute Seller Name
  const sellerName = computed(() => {
    if (isMarketPlaceEnabled && (marketPlaceItemEnabled || memberFeatureEligible.value?.marketPlaceItemEnabled) && selectedUpcId.value !== null) {
      if (
        upcData.value.upcs[selectedUpcId.value]?.marketplaceFlag &&
        upcData.value.upcs[selectedUpcId.value]?.seller?.sellerName &&
        upcData.value.upcs[selectedUpcId.value]?.availability.available
      ) {
        return formatter(upcData.value.upcs[selectedUpcId.value].seller.sellerName);
      }
      return '';
    }

    return '';
  });

  // Compute Seller Id
  const sellerId = computed(() => {
    if (isMarketPlaceEnabled && (marketPlaceItemEnabled || memberFeatureEligible.value?.marketPlaceItemEnabled) && selectedUpcId.value !== null) {
      if (upcData.value.upcs[selectedUpcId.value]?.marketplaceFlag && upcData.value.upcs[selectedUpcId.value]?.availability.available) {
        return upcData.value.upcs[selectedUpcId.value]?.seller?.sellerId;
      }
      return null;
    }

    return null;
  });

  // used only once for PDP landing page for Marketplace prods when traits selection is incomplete
  const getInitSellerName = () => {
    if (isMarketPlaceEnabled && (marketPlaceItemEnabled || memberFeatureEligible.value?.marketPlaceItemEnabled) && selectedColorId?.value) {
      const selectedSize = '0'; // auto select the size
      const defaultUpcId = upcData.value.upcMap[`${selectedColorId?.value}_${selectedSize}`];
      if (
        upcData.value.upcs[defaultUpcId]?.marketplaceFlag &&
        upcData.value.upcs[defaultUpcId]?.seller?.sellerName &&
        upcData.value.upcs[defaultUpcId]?.availability.available
      ) {
        return formatter(upcData.value.upcs[defaultUpcId].seller.sellerName);
      }
      return null;
    }

    return null;
  };

  // used only once for PDP landing page for Marketplace prods when traits selection is incomplete
  const getInitSellerId = () => {
    if (isMarketPlaceEnabled && (marketPlaceItemEnabled || memberFeatureEligible.value?.marketPlaceItemEnabled) && selectedColorId?.value) {
      const selectedSize = '0'; // auto select the size
      const defaultUpcId = upcData.value.upcMap[`${selectedColorId?.value}_${selectedSize}`];
      if (upcData.value.upcs[defaultUpcId]?.marketplaceFlag && upcData.value.upcs[defaultUpcId]?.availability.available) {
        return upcData.value.upcs[defaultUpcId]?.seller?.sellerId;
      }
      return null;
    }

    return null;
  };

  const hasSplHandlingAttrbs = computed(() => {
    const splHandlingAttrbsArray = productData?.relationships?.upcs?.[selectedUpcId.value]?.splHandlingAttrbs;
    return Array.isArray(splHandlingAttrbsArray) && splHandlingAttrbsArray.length > 0;
  });

  return {
    sellerName,
    sellerId,
    getInitSellerId,
    getInitSellerName,
    hasSplHandlingAttrbs,
  };
}

export default useMarketPlace;
